import React, { useCallback, useEffect, useState } from "react";
import useFetchBaseQuery from "../../../api/baseQueries";
import Select from "react-select";
import ClTables from "./ClTables";
import { buildCondition } from "../../../static/hook";
import { useSearchParams } from "react-router-dom";
import NotAuthorize from "../../../error/NotAuthorize";
import MenuIcon from "@mui/icons-material/Menu";
import {
  clStatusOptions,
  packageCodes,
  paymentStatusOptions,
  stStatusOptions,
} from "../../../static/dropdown";

const ClDashboard = ({
  serverURL,
  user,
  setShowSideOverlay,
  socket,
  onlineUsers,
}) => {
  const [searchParams] = useSearchParams();
  const userCode = parseInt(searchParams.get("userCode"));
  const [authState, setAuthState] = useState(false);
  const [allClReports, setAllClReports] = useState(null);
  const [allClLoading, setAllClLoading] = useState(false);
  const [allClKpiReports, setAllClKpiReports] = useState(null);
  const [allClKpiLoading, setAllClKpiLoading] = useState(false);
  const [allUserClReports, setAllUserClReports] = useState(null);
  const [allUserClLoading, setAllUserClLoading] = useState(false);
  const [studentReport, setStudentReport] = useState(null);
  const [studentReportLoading, setStudentLoading] = useState(false);
  const [clUniReport, setClUniReport] = useState(null);
  const [clUniLoading, setClUniLoading] = useState(false);
  const [filterDropdown, setFilterDropdown] = useState({
    intakeOptions: [], // Example format: [{ name: 'Intake1', value: 1 }]
    locationOptions: [],
    categoryOptions: [],
    clAppOption: [],
  });
  const [filterOptions, setFilterOptions] = useState({
    location: [],
    package: [],
    intake: [],
    checklist: [],
    course: [],
    mandatory: [],
    paymentStatus: [],
    ststatus: [],
    category: [],
    clAppStatus: [],
    clStatus: [],
  });

  const fetchBaseQuery = useFetchBaseQuery();

  const fetchFilterDropdown = useCallback(async () => {
    try {
      const queries = {
        intakeOptions:
          "SELECT IntakeCode AS 'Code', Intakename AS 'Name' FROM intakemas",
        locationOptions:
          "SELECT LocCode AS 'Code', locname AS 'Name' FROM locmas",
        categoryOptions:
          "SELECT CatCode AS 'Code', CatName AS 'Name' FROM catmas",
        clAppOption:
          "SELECT compid AS 'Code', Compname AS 'Name'  FROM commontypemas WHERE compcode = 'CourseList_ApprovalStatus'",
      };

      const fetchPromises = Object.entries(queries).map(
        async ([key, query]) => {
          const data = await fetchBaseQuery(query);
          return { key, data };
        }
      );

      const results = await Promise.all(fetchPromises);

      const updatedDropdowns = results.reduce((acc, { key, data }) => {
        if (data?.length) {
          acc[key] = data.map((item) => ({
            label: item.NAME || "Unknown",
            value: item.CODE || "N/A",
          }));
        } else {
          console.warn(`No data received for ${key}`);
        }
        return acc;
      }, {});

      if (Object.keys(updatedDropdowns).length) {
        setFilterDropdown((prev) => ({ ...prev, ...updatedDropdowns }));
      }
    } catch (error) {
      console.error("Error fetching filter dropdown options:", error.message);
    }
  }, [fetchBaseQuery]);

  const fetchStudentStatusReports = async () => {
    setStudentLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );

      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );

      const paymentCodeCondition = buildCondition(
        "s.PaymentStatus",
        filterOptions.paymentStatus
      );
      const categoryCodeCondition = buildCondition(
        "s.catCode",
        filterOptions.category
      );

      [
        intakeCondition,
        packageCondition,
        locationCondition,
        stStatusCondition,
        paymentCodeCondition,
        categoryCodeCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";
      const query = `SELECT s.ststatus, COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionSheetMas a ON a.StdCode = s.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode WHERE s.StdCode IS NOT NULL ${whereClause} GROUP BY s.ststatus`;

      const result = await fetchBaseQuery(query);

      if (result) {
        setStudentReport(result);
      }
    } catch (error) {
      setStudentLoading(false);
      console.log("error occured in fetchStudentStatusReports", error);
    } finally {
      setStudentLoading(false);
    }
  };

  const fetchClKpiReports = async () => {
    setAllClKpiLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const paymentCondition = buildCondition(
        "s.PaymentStatus",
        filterOptions.paymentStatus,
        false
      );
      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const categoryCondition = buildCondition(
        "s.catCode",
        filterOptions.category,
        true
      );
      const clAppCondition = buildCondition(
        "s.stdCLAppStatus",
        filterOptions.clAppStatus,
        true
      );
      const clStatusCondition = buildCondition(
        "s.uinliststatus",
        filterOptions.clStatus,
        true
      );
      const courseCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );

      [
        intakeCondition,
        packageCondition,
        locationCondition,
        paymentCondition,
        stStatusCondition,
        categoryCondition,
        clAppCondition,
        courseCondition,
        clStatusCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const baseWhereClause = filters.length
        ? `AND ${filters.join(" AND ")}`
        : "";
      
      const baseQuery = "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode LEFT JOIN locmas lo ON lo.locCode = s.locCode LEFT JOIN intakemas i ON s.intakeCode = i.intakeCode LEFT JOIN catmas ct ON ct.catCode = s.catCode LEFT JOIN commontypemas cm ON cm.compid = s.stdCLAppStatus"


      const queries = {
        totalApprClStudents: `${baseQuery} WHERE s.stdCLAppStatus IN (115, 116) ${baseWhereClause}`,
        totalApprClStudents: `${baseQuery} WHERE s.stdCLAppStatus IN (115, 117) ${baseWhereClause}`,
        totalNotApprClStudents: `${baseQuery} WHERE s.stdCLAppStatus IN (116) ${baseWhereClause}`,
        underEval: `${baseQuery} WHERE s.stdCLAppStatus IN (139) ${baseWhereClause}`,
        notUpdated: `${baseQuery} WHERE (s.stdCLAppStatus IS NULL OR s.stdCLAppStatus NOT IN (115, 116, 117, 139)) ${baseWhereClause}`,
      };

      const responseObj = {};

      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }

      if (responseObj) {
        setAllClKpiReports(responseObj);
      }
    } catch (error) {
      setAllClKpiLoading(false);
      console.log("error in fetchClAllReports", error);
    } finally {
      setAllClKpiLoading(false);
    }
  };

  const fetchClAllReports = async () => {
    setAllClLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const paymentCondition = buildCondition(
        "s.PaymentStatus",
        filterOptions.paymentStatus,
        false
      );
      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const categoryCondition = buildCondition(
        "s.catCode",
        filterOptions.category,
        true
      );
      const clAppCondition = buildCondition(
        "s.stdCLAppStatus",
        filterOptions.clAppStatus,
        true
      );
      const courseCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );
      const clStatusCondition = buildCondition(
        "s.uinliststatus",
        filterOptions.clStatus,
        true
      );

      [
        intakeCondition,
        packageCondition,
        locationCondition,
        paymentCondition,
        stStatusCondition,
        categoryCondition,
        clAppCondition,
        courseCondition,
        clStatusCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const baseWhereClause = filters.length
        ? `AND ${filters.join(" AND ")}`
        : "";

      const baseQuery = "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode LEFT JOIN locmas lo ON lo.locCode = s.locCode LEFT JOIN intakemas i ON s.intakeCode = i.intakeCode LEFT JOIN catmas ct ON ct.catCode = s.catCode"

      const queries = {
        totalStudents: `${baseQuery} WHERE s.StdCode IS NOT NULL ${baseWhereClause}`,
        notUpdated: `${baseQuery} WHERE (s.uinliststatus = 0 OR s.uinliststatus IS NULL) ${baseWhereClause}`,
        inProcess: `${baseQuery} WHERE s.uinliststatus = 2 ${baseWhereClause}`,
        needToDiscuss: `${baseQuery} WHERE s.uinliststatus = 10 ${baseWhereClause}`,
        clCreated: `${baseQuery} WHERE s.uinliststatus = 9 ${baseWhereClause}`,
        discussNotShared: `${baseQuery} WHERE s.uinliststatus = 11 ${baseWhereClause}`,
        discussCantShared: `${baseQuery} WHERE s.uinliststatus = 13 ${baseWhereClause}`,
        needToShared: `${baseQuery} WHERE s.uinliststatus = 12 ${baseWhereClause}`,
        shared: `${baseQuery} WHERE s.uinliststatus = 3 ${baseWhereClause}`,
        partiaFinal: `${baseQuery} WHERE s.uinliststatus = 4 ${baseWhereClause}`,
        expFinal: `${baseQuery} WHERE s.uinliststatus = 5 ${baseWhereClause}`,
        finalReopen: `${baseQuery} WHERE s.uinliststatus = 6 ${baseWhereClause}`,
        final: `${baseQuery} WHERE s.uinliststatus = 1 ${baseWhereClause}`,
      };

      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }

      setAllClReports(responseObj);
    } catch (error) {
      console.log("error in fetchClAllReports", error);
      setAllClLoading(false);
    } finally {
      setAllClLoading(false);
    }
  };

  const fetchClAllReportsUserWise = async () => {
    setAllUserClLoading(true);
    try {
      const filters = [];
      const coordinatorList = await fetchBaseQuery(
        "SELECT um.UserCode, um.UserName, um.Active FROM Usermas um WHERE um.deptcode IN (5) AND um.Active != 'InActive'"
      );

      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const paymentCondition = buildCondition(
        "s.PaymentStatus",
        filterOptions.paymentStatus,
        false
      );
      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const categoryCondition = buildCondition(
        "s.catCode",
        filterOptions.category,
        true
      );
      const clAppCondition = buildCondition(
        "s.stdCLAppStatus",
        filterOptions.clAppStatus,
        true
      );
      const clStatusCondition = buildCondition(
        "s.uinliststatus",
        filterOptions.clStatus,
        true
      );

      const courseCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );

      [
        intakeCondition,
        packageCondition,
        locationCondition,
        paymentCondition,
        stStatusCondition,
        categoryCondition,
        clAppCondition,
        courseCondition,
        clStatusCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const baseWhereClause = filters.length
        ? `AND ${filters.join(" AND ")}`
        : "";
      const baseQuery = "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode LEFT JOIN locmas lo ON lo.locCode = s.locCode LEFT JOIN intakemas i ON s.intakeCode = i.intakeCode LEFT JOIN catmas ct ON ct.catCode = s.catCode LEFT JOIN commontypemas cm ON cm.compid = s.stdCLAppStatus"
      const queries = {
        totalStudents: `${baseQuery} WHERE s.StdCode IS NOT NULL ${baseWhereClause}`,
        notUpdated: `${baseQuery} WHERE (s.uinliststatus = 0 OR s.uinliststatus IS NULL) ${baseWhereClause}`,
        inProcess: `${baseQuery} WHERE s.uinliststatus = 2 ${baseWhereClause}`,
        needToDiscuss: `${baseQuery} WHERE s.uinliststatus = 10 ${baseWhereClause}`,
        clCreated: `${baseQuery} WHERE s.uinliststatus = 9 ${baseWhereClause}`,
        discussNotShared: `${baseQuery} WHERE s.uinliststatus = 11 ${baseWhereClause}`,
        discussCantShared: `${baseQuery} WHERE s.uinliststatus = 13 ${baseWhereClause}`,
        needToShared: `${baseQuery} WHERE s.uinliststatus = 12 ${baseWhereClause}`,
        shared: `${baseQuery} WHERE s.uinliststatus = 3 ${baseWhereClause}`,
        partiaFinal: `${baseQuery} WHERE s.uinliststatus = 4 ${baseWhereClause}`,
        expFinal: `${baseQuery} WHERE s.uinliststatus = 5 ${baseWhereClause}`,
        finalReopen: `${baseQuery} WHERE s.uinliststatus = 6 ${baseWhereClause}`,
        final: `${baseQuery} WHERE s.uinliststatus = 1 ${baseWhereClause}`,
      };

      const responseObj = [];
      for (const item of coordinatorList) {
        const coordinator = item.USERCODE;
        const coordinatorData = {
          name: item?.USERNAME,
          status: item?.ACTIVE,
          details: {},
        };

        for (const [key, baseQuery] of Object.entries(queries)) {
          // Inject counsellor condition
          const finalQuery = `${baseQuery} AND um.UserCode = '${coordinator}'`;
          try {
            const result = await fetchBaseQuery(finalQuery);
            coordinatorData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1 || 0,
            };
          } catch (error) {
            console.error(`Error fetching ${key} for ${coordinator}:`, error);
            coordinatorData.details[key] = null;
          }
        }
        responseObj.push(coordinatorData);
      }
      setAllUserClReports(responseObj);
    } catch (error) {
      console.log("error in fetchClAllReports", error);
      setAllUserClLoading(false);
    } finally {
      setAllUserClLoading(false);
    }
  };

  const fetchClReportuniversityWise = async () => {
    setClUniLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const paymentCondition = buildCondition(
        "s.PaymentStatus",
        filterOptions.paymentStatus,
        false
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );

      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );

      const categoryCondition = buildCondition(
        "s.catCode",
        filterOptions.category,
        true
      );
      const clAppCondition = buildCondition(
        "s.stdCLAppStatus",
        filterOptions.clAppStatus,
        true
      );
      const courseCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );
      const clStatusCondition = buildCondition(
        "s.uinliststatus",
        filterOptions.clStatus,
        true
      );

      [
        intakeCondition,
        packageCondition,
        locationCondition,
        paymentCondition,
        stStatusCondition,
        categoryCondition,
        clAppCondition,
        courseCondition,
        clStatusCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const baseWhereClause = filters.length
        ? `AND ${filters.join(" AND ")}`
        : "";

      const queries = `
      SELECT DISTINCT a.universityName, a.deadline, a.Firststepdeadlinedatenew, a.CourseName, COUNT(CASE WHEN a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 THEN applicationid END) AS approved_applications, COUNT(CASE WHEN a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 47 THEN a.applicationid END) AS applied_applications, COUNT(CASE WHEN a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode != 47 THEN a.applicationid END) AS not_applied_applications FROM AdmissionMas a JOIN StudentMas s ON s.StdCode = a.StdCode WHERE universityName IS NOT NULL AND universityName != '' AND CourseName IS NOT NULL AND CourseName != '' ${baseWhereClause} GROUP BY a.universityName, a.deadline, a.Firststepdeadlinedatenew, a.CourseName
      `;

      let responseObj = [];
      const result = await fetchBaseQuery(queries);
      responseObj = result;
      setClUniReport(responseObj);
    } catch (error) {
      console.log("error in fetchClAllReports", error);
      setClUniLoading(false);
    } finally {
      setClUniLoading(false);
    }
  };

  const checkAuthentication = async () => {
    try {
      if (userCode === 0) {
        setAuthState(true);
        return true;
      }

      const checkAdmin = await fetchBaseQuery(
        `SELECT * FROM usermas WHERE UserCode = ${userCode} AND deptcode IN (15, 17, 16, 5, 12, 182)`
      );

      if (Array.isArray(checkAdmin) && checkAdmin.length > 0) {
        setAuthState(true);
        return true;
      }

      setAuthState(false);
      return false;
    } catch (error) {
      console.error("Error checking authentication:", error);
      setAuthState(false);
      return false;
    }
  };

  useEffect(() => {
    checkAuthentication();
    fetchFilterDropdown();
    fetchClKpiReports();
    fetchStudentStatusReports();
  }, []);

  useEffect(() => {
    fetchClAllReports();
    fetchClAllReportsUserWise();
    fetchClReportuniversityWise();
    fetchClKpiReports();
  }, [filterOptions]);
  return authState ? (
    <div className="transback h-100 overflow-auto p-3 d-flex flex-column">
      {user ? (
        <>
          <button
            className="btn text-secondary rounded-pill px-3 py-1 d-flex d-lg-none align-items-center gap-2"
            onClick={() => setShowSideOverlay(true)}
          >
            <MenuIcon />
          </button>
          <div className="headerUserDetails py-1 mb-3 d-flex justify-content-between">
            <span>CL Dashboard</span>
            <div
              className="d-flex gap-2 align-items-center"
              title={`Name: ${user.name} | Email: ${user.email}`}
            >
              <i className="fa fa-user"></i>
              <span>{user.name}</span>
            </div>
          </div>
        </>
      ) : (
        ""
      )}

      <div className="w-100 h-100 overflow-auto ">
        <div className="container-fluid">
          <div className="header py-3">
            <h3 className="display-6 fw-bold">Course Dashboard</h3>
          </div>
          <div className="w-100 mb-3">
            <div className="row row-cols-2 row-cols-lg-6 g-2 g-lg-3 mb-2">
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="package-select"
                    options={filterDropdown.intakeOptions}
                    isMulti
                    isClearable
                    placeholder="Select Intake"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        intake: selectedOptions
                          ? selectedOptions.map((opt) => parseInt(opt.value))
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="package-select"
                    options={filterDropdown.locationOptions}
                    isMulti
                    isClearable
                    placeholder="Select Branch"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        location: selectedOptions
                          ? selectedOptions.map((opt) => parseInt(opt.value))
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="payment-select"
                    options={paymentStatusOptions}
                    isMulti
                    isClearable
                    placeholder="Select Payment Status"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        paymentStatus: selectedOptions
                          ? selectedOptions.map((opt) => opt.value)
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="package-select"
                    options={packageCodes}
                    isMulti
                    isClearable
                    placeholder="Select Package"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        package: selectedOptions
                          ? selectedOptions.map((opt) => parseInt(opt.value))
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="package-select"
                    options={stStatusOptions}
                    isMulti
                    isClearable
                    placeholder="Select Student Status"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        ststatus: selectedOptions
                          ? selectedOptions.map((opt) => parseInt(opt.value))
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="category-select"
                    options={filterDropdown.categoryOptions}
                    isMulti
                    isClearable
                    placeholder="Select Category"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        category: selectedOptions
                          ? selectedOptions.map((opt) => parseInt(opt.value))
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="clApp-select"
                    options={filterDropdown.clAppOption}
                    isMulti
                    isClearable
                    placeholder="Select Cl App Status"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        clAppStatus: selectedOptions
                          ? selectedOptions.map((opt) => parseInt(opt.value))
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="course-select"
                    options={[
                      { label: "Master", value: "M" },
                      { label: "Bachelor", value: "B" },
                      { label: "Vocational", value: "V" },
                      { label: "Other", value: "B" },
                    ]}
                    isMulti
                    isClearable
                    placeholder="Select Course"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        course: selectedOptions
                          ? selectedOptions.map((opt) => opt.value)
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="col-md">
                <div className="form-group">
                  <Select
                    id="clStatus-select"
                    options={clStatusOptions}
                    isMulti
                    isClearable
                    placeholder="Select CL Status"
                    onChange={(selectedOptions) => {
                      setFilterOptions((prev) => ({
                        ...prev,
                        clStatus: selectedOptions
                          ? selectedOptions.map((opt) => parseInt(opt.value))
                          : [],
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <ClTables
              allClReports={allClReports}
              allClLoading={allClLoading}
              clUniReport={clUniReport}
              clUniLoading={clUniLoading}
              allUserClReports={allUserClReports}
              allUserClLoading={allUserClLoading}
              allClKpiReports={allClKpiReports}
              allClKpiLoading={allClKpiLoading}
              studentReport={studentReport}
              studentReportLoading={studentReportLoading}
            />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <NotAuthorize />
  );
};

export default ClDashboard;
